import React, { useEffect, useRef, useState } from 'react';
import { getDocument, type PDFDocumentProxy } from 'pdfjs-dist';
import { ensurePdfJsWorker } from './pdfjsSetup';
import './PdfCanvasViewer.css';

export interface PdfCanvasViewerProps {
  /** Blob or object URL for the PDF bytes. */
  pdfUrl: string;
  /** 1-based page index within the loaded document. */
  pageNumber?: number;
  title?: string;
  className?: string;
  renderErrorLabel?: string;
}

/** Ignore sub-pixel / scrollbar-gutter width jitter that would re-trigger a full render. */
const WIDTH_CHANGE_THRESHOLD_PX = 8;
const RESIZE_DEBOUNCE_MS = 120;

function isRenderCancelledError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  return /cancel/i.test(msg);
}

/**
 * Renders a single PDF page to canvas via PDF.js — works on mobile browsers
 * that block inline PDF in iframes (e.g. Edge on Android).
 *
 * Width is measured with a threshold + debounce so scrollbar toggles (common with
 * landscape pages) do not cause a fetch/render refresh loop.
 */
const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({
  pdfUrl,
  pageNumber = 1,
  title,
  className,
  renderErrorLabel,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const layoutWidthRef = useRef(0);
  const pdfDocRef = useRef<{ url: string; doc: PDFDocumentProxy } | null>(null);

  const [layoutWidth, setLayoutWidth] = useState(0);
  const [pdfEpoch, setPdfEpoch] = useState(0);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [rendering, setRendering] = useState(true);
  const [renderError, setRenderError] = useState(false);

  useEffect(() => {
    ensurePdfJsWorker();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const applyWidth = (w: number) => {
      if (w <= 0) return;
      if (Math.abs(w - layoutWidthRef.current) < WIDTH_CHANGE_THRESHOLD_PX) return;
      layoutWidthRef.current = w;
      setLayoutWidth(w);
    };

    const scheduleUpdate = () => {
      const w = container.clientWidth;
      if (layoutWidthRef.current === 0) {
        applyWidth(w);
        return;
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => applyWidth(container.clientWidth), RESIZE_DEBOUNCE_MS);
    };

    scheduleUpdate();
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(container);
    return () => {
      observer.disconnect();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  // Load / cache the PDF document once per url (resize only re-renders).
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingDoc(true);
      setRenderError(false);
      setRendering(true);

      const previous = pdfDocRef.current;
      if (previous && previous.url !== pdfUrl) {
        pdfDocRef.current = null;
        void previous.doc.destroy().catch(() => undefined);
      }

      try {
        if (pdfDocRef.current?.url === pdfUrl) {
          if (!cancelled) {
            setLoadingDoc(false);
            setPdfEpoch((n) => n + 1);
          }
          return;
        }

        const response = await fetch(pdfUrl, { credentials: 'include' });
        if (!response.ok) throw new Error('fetch failed');
        const data = await response.arrayBuffer();
        if (cancelled) return;

        ensurePdfJsWorker();
        const loadingTask = getDocument({ data });
        const doc = await loadingTask.promise;
        if (cancelled) {
          void doc.destroy().catch(() => undefined);
          return;
        }

        pdfDocRef.current = { url: pdfUrl, doc };
        setLoadingDoc(false);
        setPdfEpoch((n) => n + 1);
      } catch (e) {
        if (cancelled || isRenderCancelledError(e)) return;
        setRenderError(true);
        setLoadingDoc(false);
        setRendering(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  useEffect(() => {
    return () => {
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      const cached = pdfDocRef.current;
      pdfDocRef.current = null;
      if (cached) {
        void cached.doc.destroy().catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const cached = pdfDocRef.current;
    if (!canvas || !cached || cached.url !== pdfUrl || layoutWidth <= 0 || loadingDoc) {
      return undefined;
    }

    let cancelled = false;

    const render = async () => {
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      setRendering(true);
      setRenderError(false);

      try {
        const pdf = cached.doc;
        const safePage = Math.min(Math.max(1, pageNumber), pdf.numPages);
        const page = await pdf.getPage(safePage);
        if (cancelled) return;

        const scale = layoutWidth / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale });

        const context = canvas.getContext('2d');
        if (!context) throw new Error('no canvas context');

        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) setRendering(false);
      } catch (e) {
        if (cancelled || isRenderCancelledError(e)) return;
        setRenderError(true);
        setRendering(false);
      }
    };

    void render();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [pdfUrl, pageNumber, layoutWidth, pdfEpoch, loadingDoc]);

  const busy = loadingDoc || rendering;

  return (
    <div
      ref={containerRef}
      className={['pdf-canvas-viewer', className].filter(Boolean).join(' ')}
      aria-busy={busy}
    >
      {busy ? <div className="pdf-canvas-viewer__loading">読み込み中...</div> : null}
      {renderError ? (
        <p className="pdf-canvas-viewer__error" role="alert">
          {renderErrorLabel ?? 'Preview unavailable.'}
        </p>
      ) : null}
      <canvas ref={canvasRef} className="pdf-canvas-viewer__canvas" aria-label={title} />
    </div>
  );
};

export default PdfCanvasViewer;
